import React, { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

import { useRemoveFlight } from '~/hooks/apis/accommodation/useBundles';

import { Bundle, BundleOffer } from '~/services/AccommodationService';

import AccommodationBundleFlightDialog from '../dialogs/AccommodationBundleFlightDialog';

interface Props {
  bundle: Bundle;
}

// `null` closes the dialog; `{}` opens it to add a new flight.
type DialogState = { offer?: BundleOffer } | null;

function AccommodationBundleFlightsTab({ bundle }: Props) {
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const removeFlightMutation = useRemoveFlight();

  const flights = (bundle.offers ?? []).filter((o) => o.entityType === 'FLIGHT');

  const handleRemoveFlight = (offer: BundleOffer) => {
    if (!window.confirm(`Remove flights to ${offer.entityId}?`)) {
      return;
    }
    removeFlightMutation.mutate({ bundleId: bundle.id, offerId: offer.id });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={() => setDialogState({})}>
          + Add Flights
        </Button>
      </Box>

      {flights.length === 0 ? (
        <Alert severity="info">No flights attached. Click "+ Add Flights" to add a destination.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Destination</TableCell>
              <TableCell>Origins</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flights.map((offer) => (
              <TableRow key={offer.id}>
                <TableCell>
                  <Chip label={offer.entityId} color="primary" size="small" />
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {(offer.originAirports ?? []).map((code) => (
                      <Chip key={code} label={code} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>{offer.experienceName || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setDialogState({ offer })} aria-label="Edit flight">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFlight(offer)}
                    disabled={removeFlightMutation.isPending}
                    aria-label="Remove flight"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {dialogState && (
        <AccommodationBundleFlightDialog
          bundleId={bundle.id}
          offer={dialogState.offer}
          open
          onClose={() => setDialogState(null)}
        />
      )}
    </Box>
  );
}

export default AccommodationBundleFlightsTab;
